import React, {FunctionComponent, useMemo} from "react";
import {Box, Button, Chip, Divider, LinearProgress, Paper, Stack, Typography} from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import East from "@mui/icons-material/East";
import {Skeleton} from "@atiautomacao/ati-ui-library";
import {NavigationEquipment, NavigationPowerStation, NavigationSkid} from "../../../../Shared/Types/Navigation";
import {getIconColor} from "../../../../Shared/Components/Layout/menu/helpers/getIconColor";
import {MenuItemStatus} from "../../../../Shared/Components/Layout/menu/types/MenuItemStatus";
import {FindTeleObjectBessReactQuery, TeleObjectBessData} from "./BatteryReactQuery";
import {FindActiveTeleObjectAlarmsByEquipmentId, getAlarmedTeleObjectIdSet} from "../inverters/InverterReactQuery";
import {useNavigate} from "react-router-dom";
import {useAppDispatch} from "../../../../Config/Hooks";
import {setEquipment} from "../../DashboardReducer";
import {SystemRoutes} from "../../../../Utils/RouteUtils";
import BessPcsModulesSection from "../../BessDetail/BessPcsModulesSection";

const SOC_COLOR = "#1bb483";
const ALARM_BAR_COLOR = getIconColor("Alarmado - D32F2F" as MenuItemStatus);
const POWER_COLOR = "#2D5DE7";
const TRACK_COLOR = "#d9dcdd";
const MEASUREMENT_TEXT_COLOR = "#47484c";
const NO_DATA = "-";


const DATA_TYPE_SHORT_NAME = {
    SOC: "SOC_BESS_CARG",
    ACTIVE_POWER: "POT_ATIV",
    AVAILABLE_CAPACITY: "CAP_DISP",
    CYCLE_COUNT: "CICLOS",
    REACTIVE_POWER: "POT_REAT",
    APPARENT_POWER: "POT_AP",
    POWER_FACTOR: "FP",
    DC_VOLTAGE: "V_DC",
    DC_CURRENT: "A_DC",
    TEMPERATURE: "TEMP",
    FREQUENCY: "FREQ",
    EXPORTED_ENERGY: "GEN_ENERGY",
    IMPORTED_ENERGY: "CONS_ENERGY",
    BMS_BATTERY_CHARGE: "BAT",
} as const;

const formatValue = (value: number | null | undefined, suffix: string, decimals = 1) =>
    (value === null || value === undefined) ? NO_DATA : `${value.toFixed(decimals)}${suffix}`;

interface BessValue {
    value: number | null;
    unity: string;
}

const findBessValue = (bessData: TeleObjectBessData[], shortName: string): BessValue => {
    const item = bessData.find((item) => item.shortName === shortName);
    return {
        value: item?.tmValue ?? null,
        unity: item?.unityName ?? "",
    };
};

interface PowerGaugeBarProps {
    value: number | null;
    minPower: number | null;
    maxPower: number | null;
}

const PowerGaugeBar: FunctionComponent<PowerGaugeBarProps> = ({value, minPower, maxPower}) => {
    const hasScale = minPower !== null && minPower !== undefined && maxPower !== null && maxPower !== undefined;
    let leftFill = 0;
    let rightFill = 0;

    if (hasScale && value !== null && value !== undefined) {
        if (value < 0) {
            const scale = Math.abs(minPower as number) || 1;
            leftFill = Math.min(100, (Math.abs(value) / scale) * 100);
        } else if (value > 0) {
            const scale = (maxPower as number) || 1;
            rightFill = Math.min(100, (value / scale) * 100);
        }
    }

    return (
        <Box sx={{position: "relative", height: 12, display: "flex", alignItems: "center"}}>
            <Box sx={{display: "flex", width: "100%", height: 4, borderRadius: 4, bgcolor: TRACK_COLOR, overflow: "hidden"}}>
                <Box sx={{width: "50%", height: "100%", display: "flex", justifyContent: "flex-end"}}>
                    <Box sx={{width: `${leftFill}%`, height: "100%", bgcolor: POWER_COLOR}}/>
                </Box>
                <Box sx={{width: "50%", height: "100%"}}>
                    <Box sx={{width: `${rightFill}%`, height: "100%", bgcolor: POWER_COLOR}}/>
                </Box>
            </Box>
            <Box sx={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                width: 2,
                height: 12,
                borderRadius: 1,
                bgcolor: "#8b8d91",
            }}/>
        </Box>
    );
};

interface ElectricalMeasurementRow {
    label: string;
    value: string;
}

const ElectricalMeasurementsList: FunctionComponent<{ bessData: TeleObjectBessData[] }> = ({bessData}) => {
    const {value: reactivePower, unity: reactivePowerUnity} = findBessValue(bessData, DATA_TYPE_SHORT_NAME.REACTIVE_POWER);
    const {value: apparentPower, unity: apparentPowerUnity} = findBessValue(bessData, DATA_TYPE_SHORT_NAME.APPARENT_POWER);
    const powerFactor = findBessValue(bessData, DATA_TYPE_SHORT_NAME.POWER_FACTOR).value;
    const {value: dcVoltage, unity: dcVoltageUnity} = findBessValue(bessData, DATA_TYPE_SHORT_NAME.DC_VOLTAGE);
    const {value: dcCurrent, unity: dcCurrentUnity} = findBessValue(bessData, DATA_TYPE_SHORT_NAME.DC_CURRENT);
    const {value: temperature, unity: temperatureUnity} = findBessValue(bessData, DATA_TYPE_SHORT_NAME.TEMPERATURE);
    const {value: frequency, unity: frequencyUnity} = findBessValue(bessData, DATA_TYPE_SHORT_NAME.FREQUENCY);
    const {value: exportedEnergy, unity: exportedEnergyUnity} = findBessValue(bessData, DATA_TYPE_SHORT_NAME.EXPORTED_ENERGY);
    const {value: importedEnergy, unity: importedEnergyUnity} = findBessValue(bessData, DATA_TYPE_SHORT_NAME.IMPORTED_ENERGY);

    const apparentPowerAndPowerFactor = (apparentPower === null && powerFactor === null)
        ? NO_DATA
        : `${formatValue(apparentPower, ` ${apparentPowerUnity}`)} / FP ${formatValue(powerFactor, "", 3)}`;

    const rows: ElectricalMeasurementRow[] = [
        {label: "Potência Reativa", value: formatValue(reactivePower, ` ${reactivePowerUnity}`, 2)},
        {label: "Potência Aparente / Fator de Potência", value: apparentPowerAndPowerFactor},
        {label: "Tensão DC", value: formatValue(dcVoltage, ` ${dcVoltageUnity}`)},
        {label: "Corrente DC", value: formatValue(dcCurrent, ` ${dcCurrentUnity}`)},
        {label: "Temperatura Máxima do Módulo", value: formatValue(temperature, ` ${temperatureUnity}`)},
        {label: "Frequência", value: formatValue(frequency, ` ${frequencyUnity}`, 3)},
        {label: "Energia Exportada", value: formatValue(exportedEnergy, ` ${exportedEnergyUnity}`, 3)},
        {label: "Energia Importada", value: formatValue(importedEnergy, ` ${importedEnergyUnity}`, 3)},
    ];

    return (
        <Stack divider={<Divider/>} spacing={1}>
            {rows.map((row) => (
                <Box key={row.label} sx={{display: "flex", justifyContent: "space-between", py: 0.5}}>
                    <Typography variant="body2" sx={{color: MEASUREMENT_TEXT_COLOR}}>{row.label}</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{color: MEASUREMENT_TEXT_COLOR}}>
                        {row.value}
                    </Typography>
                </Box>
            ))}
        </Stack>
    );
};

const BmsRacksSection: FunctionComponent<{ bmsRacks: TeleObjectBessData[]; alarmedTeleObjectIds: Set<number> }> = ({bmsRacks, alarmedTeleObjectIds}) => {
    if (bmsRacks.length === 0) {
        return null;
    }

    return (
        <Box>
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1}}>
                <Typography variant="subtitle1" fontWeight={600}>RACKS - SOC por BMS</Typography>
                <Typography variant="body2" sx={{color: "text.secondary"}}>
                    Para ver todos acesse os detalhes do BESS
                </Typography>
            </Box>
            <Stack spacing={1}>
                {bmsRacks.map((rack, index) => {
                    const label = `BMS${(rack.teleObjectDataTypeReference ? rack.teleObjectDataTypeReference.toString().padStart(2, "0") : "")}`;
                    const hasValue = rack.tmValue !== null && rack.tmValue !== undefined;
                    const value: number =  hasValue ? (rack.tmValue as number) : 0;
                    const isAlarmed = rack.teleObjectId != null && alarmedTeleObjectIds.has(Number(rack.teleObjectId));
                    return (
                        <Box key={`${rack.teleObjectId ?? index}`} sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                            <Typography variant="body2" sx={{color: MEASUREMENT_TEXT_COLOR, width: 56, flexShrink: 0}}>
                                {label}
                            </Typography>
                            { hasValue ? (
                                <LinearProgress
                                variant="determinate"
                                value={Math.min(100, Math.max(0, value))}
                                sx={{
                                    flexGrow: 1,
                                    height: 4,
                                    borderRadius: 999,
                                    bgcolor: TRACK_COLOR,
                                    "& .MuiLinearProgress-bar": {backgroundColor: isAlarmed ? ALARM_BAR_COLOR : SOC_COLOR, borderRadius: 999},
                                }}
                            />
                            ) : (
                                <Typography variant="body2" sx={{color: MEASUREMENT_TEXT_COLOR}}>
                                    {NO_DATA}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
};

interface BatteryCardProps {
    powerPlant: NavigationPowerStation;
    equipment: NavigationEquipment;
    skid?: NavigationSkid;
    minPower: number | null;
    maxPower: number | null;
}

const BatteryCard: FunctionComponent<BatteryCardProps> = ({powerPlant, equipment, skid, minPower, maxPower}) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const query = FindTeleObjectBessReactQuery(equipment?.id, powerPlant?.id);
    // Alarmes ativos por equipamento (mesma regra da detalhada: severidade != "Não se aplica/Evento").
    const activeAlarmsQuery = FindActiveTeleObjectAlarmsByEquipmentId(equipment?.id ?? 0);
    const alarmedTeleObjectIds = useMemo(
        () => getAlarmedTeleObjectIdSet(activeAlarmsQuery.data),
        [activeAlarmsQuery.data]
    );
    const bessData: TeleObjectBessData[] = query.data?.data ?? [];

    const {value: soc, unity: socUnity} = findBessValue(bessData, DATA_TYPE_SHORT_NAME.SOC);
    const {value: activePower, unity: activePowerUnity} = findBessValue(bessData, DATA_TYPE_SHORT_NAME.ACTIVE_POWER);
    const {value: availableCapacity, unity: capacityUnity} = findBessValue(bessData, DATA_TYPE_SHORT_NAME.AVAILABLE_CAPACITY);
    const cycleCount = findBessValue(bessData, DATA_TYPE_SHORT_NAME.CYCLE_COUNT).value;
    const bmsRacks = bessData.filter((item) => item.shortName === DATA_TYPE_SHORT_NAME.BMS_BATTERY_CHARGE).sort((a, b) => (a.teleObjectDataTypeReference ?? 0) - (b.teleObjectDataTypeReference ?? 0));

    const handleDetails = (event: React.MouseEvent<HTMLButtonElement>, equipment: NavigationEquipment) => {
        dispatch(setEquipment(equipment));
        navigate(SystemRoutes.DASHBOARD_BESS_DETAIL.replace(":equipmentId", String(equipment.id)));
    };

    const dotColor = getIconColor((equipment?.etsStatus ?? "") as MenuItemStatus);

    const powerPercentOfScale = (() => {
        if (activePower === null || activePower === undefined) return null;
        if (minPower === null || minPower === undefined || maxPower === null || maxPower === undefined) return null;
        if (activePower < 0) {
            return Math.round((Math.abs(activePower) / (Math.abs(minPower) || 1)) * 100);
        }
        if (activePower > 0) {
            return Math.round((activePower / (maxPower || 1)) * 100);
        }
        return 0;
    })();

    return (
        <Paper variant="outlined" sx={{borderRadius: 2, p: 2.5}}>
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <FiberManualRecordIcon sx={{color: dotColor, fontSize: 24}}/>
                    <Typography variant="h6" sx={{fontWeight: 500}}>{equipment?.name}</Typography>
                </Box>
                <Button
                    variant="text"
                    color="primary"
                    size="small"
                    startIcon={<East fontSize="small"/>}
                    onClick={(event) => handleDetails(event, equipment)}
                    sx={{color: "#2D5DE7", textTransform: "uppercase", letterSpacing: "0.46px", "&:hover": {backgroundColor: "transparent"}}}
                >
                    Ver detalhes
                </Button>
            </Box>
            <Divider sx={{mb: 2}}/>

            {query.isLoading ? (
                <Skeleton animation="wave" height={480} variant="rounded" width="100%"/>
            ) : (
                <Stack spacing={3}>
                    <Box>
                        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{color: theme => theme.palette.text.primary}}>
                                Estado de carga (SOC)
                            </Typography>
                        </Box>
                        <Typography variant="h5" fontWeight={700} sx={{color: soc !== null && soc !== undefined ? SOC_COLOR : "text.disabled", mb: 0.5}}>
                            {formatValue(soc, socUnity)}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={soc !== null && soc !== undefined ? Math.min(100, Math.max(0, soc)) : 0}
                            sx={{
                                height: 4,
                                borderRadius: 999,
                                bgcolor: TRACK_COLOR,
                                mb: 1,
                                "& .MuiLinearProgress-bar": {backgroundColor: SOC_COLOR, borderRadius: 999},
                            }}
                        />
                        <Box sx={{display: "flex", justifyContent: "space-between"}}>
                            <Typography variant="caption" sx={{color: "text.secondary"}}>
                                Capacidade disponível: {formatValue(availableCapacity, ` ${capacityUnity}`, 2)}
                            </Typography>
                            <Typography variant="caption" sx={{color: "text.secondary"}}>
                                Número de ciclos: {cycleCount !== null ? Math.round(cycleCount) : NO_DATA}
                            </Typography>
                        </Box>
                    </Box>

                    <Box>
                        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                            <Typography variant="subtitle1" fontWeight={600}>Potência Ativa</Typography>
                            {powerPercentOfScale !== null && (
                                <Chip
                                    size="small"
                                    label={`${powerPercentOfScale}% da carga total`}
                                    sx={{color: "#fff", bgcolor: POWER_COLOR, fontWeight: 600, borderRadius: 999}}
                                />
                            )}
                        </Box>
                        <Typography variant="h5" fontWeight={700} sx={{color: activePower !== null && activePower !== undefined ? POWER_COLOR : "text.disabled", mb: 0.5}}>
                            {formatValue(activePower, ` ${activePowerUnity}`)}
                        </Typography>
                        <Box sx={{mb: 1}}>
                            <PowerGaugeBar value={activePower} minPower={minPower} maxPower={maxPower}/>
                        </Box>
                        <Box sx={{display: "flex", justifyContent: "space-between"}}>
                            <Typography variant="caption" sx={{color: "text.secondary"}}>
                                Descarga máx. {minPower !== null && minPower !== undefined ? `${minPower} kW` : NO_DATA}
                            </Typography>
                            <Typography variant="caption" sx={{color: "text.secondary"}}>
                                Carga máx. {maxPower !== null && maxPower !== undefined ? `+${maxPower} kW` : NO_DATA}
                            </Typography>
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" fontWeight={600} sx={{mb: 1}}>
                            Medições elétricas
                        </Typography>
                        <ElectricalMeasurementsList bessData={bessData}/>
                    </Box>

                    <BmsRacksSection bmsRacks={bmsRacks} alarmedTeleObjectIds={alarmedTeleObjectIds}/>

                    <BessPcsModulesSection equipmentId={equipment.id} variant="simple" />

                </Stack>
            )}
        </Paper>
    );
};

export default BatteryCard;
